import { FILTERS_WITH_AND_COMBINATION } from '../constants';
import { SearchFacet, SearchFilters, SearchFilterValue } from '../model';
import { isFilterHierarchy, isFilterRange, isFilterTerms } from './typeGuards';

const isAndCombinationFilter = (fieldName: string, filterValue?: SearchFilterValue) => {
    return FILTERS_WITH_AND_COMBINATION.includes(fieldName) && !!filterValue && Array.isArray(filterValue);
};

/**
 * Splits a string by commas, but only if the comma is NOT preceded by a space.
 * For example:
 *   "value1,value2" → ["value1", "value2"]
 *   "value1, with comma,value2" → ["value1, with comma", "value2"]
 *   "Taip, gamintojui patvirtinus veiks ir „Telia" 5G tinkle,Taip" → ["Taip, gamintojui patvirtinus veiks ir „Telia" 5G tinkle", "Taip"]
 */
export const splitByMainCommas = (value: string): string[] => value.split(/,(?! )/).map((s) => s.trim());

/**
 * Updates some of the filters to AND combination logic (filters list in constants.ts `FILTERS_WITH_AND_COMBINATION`)
 * @param filters all initial filters
 */
export const extractANDFilters = (filters?: SearchFilters): SearchFilters => {
    if (!filters || !Object.keys(filters).length) {
        return filters;
    }

    const orOnlyFilters = Object.keys(filters).reduce<SearchFilters>((acc, fieldName) => {
        if (!isAndCombinationFilter(fieldName, filters[fieldName])) {
            acc[fieldName] = filters[fieldName];
        }

        return acc;
    }, {});

    const andOnlyFilters = FILTERS_WITH_AND_COMBINATION.reduce<SearchFilters['_and']>((acc, fieldName) => {
        const filterValues = filters[fieldName];
        if (!filterValues || !Array.isArray(filterValues)) {
            return acc;
        }

        const mappedValues = filterValues.map((filterValue) => ({ [fieldName]: [filterValue] }));

        return [...acc, ...mappedValues];
    }, []);

    const andFilters = [...(filters['_and'] || []), ...andOnlyFilters];

    return { ...orOnlyFilters, ...(andFilters.length > 0 ? { _and: andFilters } : {}) } as SearchFilters;
};

export const formatFilterToQueryParamValue = (facets: SearchFacet[], fieldName: string, filter?: SearchFilterValue) => {
    const facet = facets.find(({ key }) => key === fieldName);

    if (!facet || !filter) {
        return null;
    }

    if ((facet.type === 'stats' || facet.type === 'range') && isFilterRange(filter)) {
        return [filter.gte, filter.lte].join(',');
    }

    if (facet.type === 'hierarchy' && isFilterHierarchy(filter)) {
        return filter.terms.map((term) => encodeURIComponent(term)).join(',');
    }

    if (facet.type === 'terms' && isFilterTerms(filter)) {
        return filter.map((term) => term).join(',');
    }

    return null;
};
